import React from 'react';
import {Image, StyleProp, StyleSheet, Text, TextStyle, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import config, {Brand} from '../../config';
import {
  control,
  lineHeight,
  resolveWeight,
  space,
  type,
  universal,
} from '../../design-system/tokens';
import {AppRoute} from '../../models/AppRoute';
import {InformationContentType} from '../../models/navigationTypes';
import * as RootNavigation from '../../navigation/RootNavigation';
import {useStores} from '../../stores/use-stores';

import AppStoreBadgeBlack from './assets/app-store-badge-black.svg';
import AppStoreBadgeWhite from './assets/app-store-badge-white.svg';
import drinkWiseWhite from './assets/drinkwise-white.png';
import FacebookBlack from './assets/facebook-black.svg';
import FacebookWhite from './assets/facebook-white.svg';
import googlePlayBadge from './assets/google-play-badge.png';
import GooglePlayBadgeWhite from './assets/google-play-badge-white.svg';
import InstagramBlack from './assets/instagram-black.svg';
import InstagramWhite from './assets/instagram-white.svg';
import {brandChrome} from './brandChrome';
import {chromeType} from './chromeType';

/**
 * Web footer (DIS-1841), ported from the DIS-1859 Storybook prototype
 * (`prototypes/siteChrome/DesktopFooter.tsx`). Figma footer/mob 161:8697, footer/tab 161:8571,
 * footer/desk 476:7181, footer/wide-desk 475:22813. Web only: the external links are DOM anchors.
 */

const darkGround = brandChrome.onGround === universal.white;
const Instagram = darkGround ? InstagramWhite : InstagramBlack;
const Facebook = darkGround ? FacebookWhite : FacebookBlack;
const lightBadges = brandChrome.storeBadge === 'light';
const AppStoreBadge = lightBadges ? AppStoreBadgeWhite : AppStoreBadgeBlack;

const SOCIAL_ICON = 20;
/** Each social link is a control.inputHeight (44) square; this inset keeps the icon where it was. */
const SOCIAL_HIT_INSET = (control.inputHeight - SOCIAL_ICON) / 2;
// SHORTCUT: column max width and DrinkWise box are layout literals from footer/desk 160:6222 /
// 160:6628. Upgrade path: layout tokens from DIS-1735.
const COLUMN_MAX_WIDTH = 310;
const DRINKWISE_WIDTH = 90;
// SHORTCUT: 19 is off the space scale; Figma 161:7741 draws it. Upgrade path: design moves it
// to space.s16 or space.s20.
const RSA_GAP = 19;
const DRINKWISE_HEIGHT = 32;
// SHORTCUT: 20 is off the space scale; the DIS-1946 footer proposal asks for 20 px mobile side
// padding in the legal bar. Upgrade path: a space.s20 step or layout.mobile gutter (DIS-1735).
const MOBILE_LEGAL_PADDING_X = 20;
// SHORTCUT: 10 is off the radius scale (control 4, card 8); chosen by eye for the version tab
// (2026-10-30). Upgrade path: a radius step from DIS-1735, or move to radius.card.
const VERSION_TAB_RADIUS = 10;
// footer/mob 161:8697 stacks every band: link columns 332 wide, gap 40, heading → list 16; the
// RSA block and the legal links stack too. The frame centres the 332 column in 375 (x 21.5), but
// the mobile layout runs 320–1023px, so the columns left-align on the 16px gutter (DIS-2029).
// SHORTCUT: 332 is a layout literal (verdict "layout literal"). Upgrade path: DIS-1735 tokens.
const MOBILE_COLUMN_WIDTH = 332;
// SHORTCUT: no content-width token. spec.json tokenGaps verdict for 1312: "new token proposal:
// recurs on header, dropdown and footer". Upgrade path: layout.desktop.contentMax 1312.
const CONTENT_MAX_WIDTH = 1312;
/** 1440 at the Figma desktop frame: the content column plus its two 64 insets. */
const PAGE_MAX_WIDTH = CONTENT_MAX_WIDTH + 2 * space.s64;

// Store badges: official vendor artwork, shown at the Figma badge height of 40, which is also
// Apple's on-screen minimum. Apple's SVG viewBox is 119.66407 × 40. Google's PNG is 646 × 250
// with transparent padding of 41 on every side around a 564 × 168 badge (alpha bounds measured
// with pngjs), so its box is cropped to the visible badge. Both badges share the Apple width
// (Figma draws both 120 wide); Google's wider aspect then makes it ~35.6 tall, centred in a
// 40-tall slot so the column spacing still matches Figma. The light Google badge is the Figma
// drawing (476:7440), 120 × 40 like the slot, so it needs no crop.
const BADGE_HEIGHT = 40;
const APPLE_VIEWBOX = {width: 119.66407, height: 40};
const APPLE_BADGE_WIDTH = (BADGE_HEIGHT * APPLE_VIEWBOX.width) / APPLE_VIEWBOX.height;
const GOOGLE_PNG = {width: 646, height: 250, inset: 41};
const GOOGLE_LIGHT_VIEWBOX = {width: 120, height: 40};
const googleScale = APPLE_BADGE_WIDTH / (GOOGLE_PNG.width - 2 * GOOGLE_PNG.inset);

const APP_STORE_URL = config.APP_STORE_ID
  ? `https://apps.apple.com/au/app/id${config.APP_STORE_ID}`
  : null;
const PLAY_STORE_URL = config.PLAY_STORE_PACKAGE
  ? `https://play.google.com/store/apps/details?id=${config.PLAY_STORE_PACKAGE}`
  : null;

type Social = 'instagram' | 'facebook';
/** Render order, per footer/desk. */
const SOCIAL_ORDER: Social[] = ['instagram', 'facebook'];
const socialLinks: {[key in Brand]?: {[key in Social]?: string}} = {
  [Brand.BOTTLEMART]: {
    facebook: 'https://www.facebook.com/Bottlemart',
    instagram: 'https://www.instagram.com/bottlemartau',
  },
  [Brand.SIPNSAVE]: {
    facebook: 'https://www.facebook.com/pages/Sipn-Save/101508726608993',
    instagram: 'https://www.instagram.com/sipn.save',
  },
  [Brand.HARRY_BROWN]: {
    facebook: 'https://www.facebook.com/harrybrownliquor',
    instagram: 'https://www.instagram.com/harrybrownliquor',
  },
  [Brand.THIRSTY_CAMEL]: {
    facebook: 'https://www.facebook.com/ThirstyCamelWA',
    instagram: 'https://www.instagram.com/thirstycamel_wa/',
  },
};
const SOCIAL_LABEL: Record<Social, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
};

type FooterLink = {label: string; route: AppRoute; params?: Record<string, unknown>};

const info = (label: string, target: InformationContentType): FooterLink => ({
  label,
  route: AppRoute.INFORMATION_CONTENT,
  params: {target},
});

/** Footer content (taxonomy 346:32745 … 346:32765). Column order follows the footer design. */
const supportLinks: FooterLink[] = [
  {label: 'Contact Us', route: AppRoute.CONTACT_US_SUBMIT_TICKET},
  info('Returns & Exchanges', InformationContentType.RETURNS_AND_EXCHANGES),
  info('Delivery & Pick Up FAQs', InformationContentType.DELIVERY_AND_PICKUP_FAQS),
];
// footer/desk 476:7181 column "Discover"; "Inspiration" is the taxonomy's "Discover More [Blog]".
const discoverLinks: FooterLink[] = [
  info('About Us', InformationContentType.ABOUT_US),
  {label: 'Inspiration', route: AppRoute.DISCOVER},
  // DIS-1920: moved here from the account menu, same targets. Gift Cards: allStoresAppRoutes.tsx
  // registers GIFT_CARDS_OPTIONS only under FF_IS_GIFT_CARDS_ENABLED.
  {label: 'Catalogue', route: AppRoute.CATALOGUE},
  {label: 'Promotions', route: AppRoute.PROMOTIONS},
  ...(config.FF_IS_GIFT_CARDS_ENABLED
    ? [{label: 'Gift Cards', route: AppRoute.GIFT_CARDS_OPTIONS}]
    : []),
];
// The footer design draws these two under the RSA sentence, in this order.
const rsaLinks: FooterLink[] = [
  {label: 'Self Exclusion', route: AppRoute.SELF_EXCLUSION},
  {label: 'Responsible Service', route: AppRoute.RESPONSIBLE_SERVICE},
];
// Registered only in liquorStoreAppRoutes; getRoutesForStoreType drops them for a gift-card
// store, so the footer hides them there instead of rendering links that do nothing.
const LIQUOR_STORE_ONLY = new Set<AppRoute>([
  AppRoute.DISCOVER,
  AppRoute.CATALOGUE,
  AppRoute.PROMOTIONS,
  AppRoute.SELF_EXCLUSION,
  AppRoute.RESPONSIBLE_SERVICE,
]);
const legalLinks: FooterLink[] = [
  info('Terms & Conditions - Online Ordering', InformationContentType.TERMS_AND_CONDITIONS),
  info('Terms & Conditions - Loyalty', InformationContentType.REWARDS_PROGRAM_TERMS_AND_CONDITIONS),
  info('Privacy Policy', InformationContentType.PRIVACY_POLICY),
];

/** A DOM anchor's style: flex, so an inline SVG adds no line-box gap under itself. */
const anchorStyle = {display: 'flex'} as const;

const ExternalLink = ({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) => (
  <a href={href} target="_blank" rel="noreferrer" aria-label={label} style={anchorStyle}>
    {children}
  </a>
);

const Link = ({link, style}: {link: FooterLink; style: StyleProp<TextStyle>}) => (
  <Text
    accessibilityRole="link"
    style={style}
    onPress={() => RootNavigation.navigate(link.route, link.params)}>
    {link.label}
  </Text>
);

type ColumnProps = {heading: string; mobile: boolean; children: React.ReactNode};

const Column = ({heading, mobile, children}: ColumnProps) => (
  <View style={[styles.column, mobile ? mobileStyles.column : styles.desktopColumn]}>
    <Text style={styles.heading}>{heading}</Text>
    {children}
  </View>
);

const LinkList = ({
  heading,
  links,
  mobile,
}: {
  heading: string;
  links: FooterLink[];
  mobile: boolean;
}) => (
  <Column heading={heading} mobile={mobile}>
    <View style={styles.links}>
      {links.map(link => (
        <Link key={link.label} link={link} style={styles.link} />
      ))}
    </View>
  </Column>
);

const SocialIcon = ({social}: {social: Social}) =>
  social === 'instagram' ? (
    <Instagram width={SOCIAL_ICON} height={SOCIAL_ICON} />
  ) : (
    <Facebook width={SOCIAL_ICON} height={SOCIAL_ICON} />
  );

const Socials = () => {
  const links = socialLinks[config.BASE_TENANT] ?? {};
  return (
    <View style={styles.socials}>
      {SOCIAL_ORDER.map(social => {
        const href = links[social];
        return href ? (
          <ExternalLink key={social} href={href} label={SOCIAL_LABEL[social]}>
            <View style={styles.socialTarget}>
              <SocialIcon social={social} />
            </View>
          </ExternalLink>
        ) : null;
      })}
    </View>
  );
};

/** Side by side, wrapping to a stack when the column is too narrow for both (DIS-1946). */
const StoreBadges = () => (
  <View style={styles.badges}>
    {!!APP_STORE_URL && (
      <ExternalLink href={APP_STORE_URL} label="Download on the App Store">
        <AppStoreBadge
          width={APPLE_BADGE_WIDTH}
          height={BADGE_HEIGHT}
          // Web svgr strips the viewBox; pass it so the artwork scales instead of clipping.
          viewBox={`0 0 ${APPLE_VIEWBOX.width} ${APPLE_VIEWBOX.height}`}
        />
      </ExternalLink>
    )}
    {!!PLAY_STORE_URL && (
      <ExternalLink href={PLAY_STORE_URL} label="Get it on Google Play">
        {lightBadges ? (
          <GooglePlayBadgeWhite
            width={APPLE_BADGE_WIDTH}
            height={BADGE_HEIGHT}
            // As above: web svgr strips the viewBox.
            viewBox={`0 0 ${GOOGLE_LIGHT_VIEWBOX.width} ${GOOGLE_LIGHT_VIEWBOX.height}`}
          />
        ) : (
          <View style={styles.googleBadgeSlot}>
            <View style={styles.googleBadgeBox}>
              <Image source={googlePlayBadge} style={styles.googleBadgeImage} />
            </View>
          </View>
        )}
      </ExternalLink>
    )}
  </View>
);

export type WebFooterProps = {layout?: 'desktop' | 'mobile'};

/** The page's last line: a full-width line with a centred outline tab standing on it. */
const VersionTab = ({mobile}: {mobile: boolean}) => {
  // DIS-1946: `v<version>`, plus the env name off prod. The prod env files set no ENV.
  // A local web build takes APP_VERSION from `git describe`, which already starts with `v`.
  const version = config.APP_VERSION.replace(/^v/i, '');
  return (
    <View style={styles.versionRow} testID="web-footer-version-row">
      {/* The tab sits at the content's right edge: the same inset as the bands above. */}
      <View style={[styles.inner, styles.versionInner, mobile && mobileStyles.versionInner]}>
        <View
          style={styles.versionTab}
          accessible
          accessibilityLabel={`Version ${version}${config.ENV ? `, ${config.ENV}` : ''}`}
          testID="web-footer-version">
          <Text style={styles.versionText}>{`v${version}`}</Text>
          {config.ENV ? (
            <Text style={[styles.versionText, styles.envText]} testID="web-footer-env-badge">
              {config.ENV}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
};

function WebFooter({layout = 'desktop'}: WebFooterProps) {
  const {shopSelectionStore} = useStores();
  const licence: string | null = shopSelectionStore.liquorLicense;
  const routable = (links: FooterLink[]) =>
    shopSelectionStore.isSelectedStoreGiftCardStore
      ? links.filter(link => !LIQUOR_STORE_ONLY.has(link.route))
      : links;
  const rsaLinksShown = routable(rsaLinks);
  const mobile = layout === 'mobile';
  const showBadges = !!APP_STORE_URL || !!PLAY_STORE_URL;

  return (
    <View style={styles.root} testID="web-footer">
      <View style={[styles.band, styles.divided]}>
        <View
          style={[styles.inner, styles.linksBand, mobile && mobileStyles.linksBand]}
          testID="web-footer-content">
          <Column heading="Follow us" mobile={mobile}>
            <Socials />
          </Column>
          <LinkList heading="Support" links={supportLinks} mobile={mobile} />
          <LinkList heading="Discover" links={routable(discoverLinks)} mobile={mobile} />
          {showBadges ? (
            <View
              style={[
                styles.column,
                styles.appColumn,
                mobile ? mobileStyles.column : styles.desktopColumn,
              ]}>
              <Text style={styles.heading}>Download our app</Text>
              <StoreBadges />
            </View>
          ) : null}
        </View>
      </View>
      <View style={[styles.band, styles.divided]}>
        <View style={[styles.inner, styles.rsaBand, mobile && mobileStyles.stack]}>
          <Image
            source={drinkWiseWhite}
            accessibilityLabel="DrinkWise"
            resizeMode="contain"
            // The white PNG takes the brand's onGround colour: black on SNS and TC, whose light
            // grounds give white 1.35:1 and 1.82:1 against black 15.59:1 and 11.52:1.
            // SHORTCUT: tinted, not official dark DrinkWise artwork. Upgrade path: DrinkWise
            // supplies the dark logo.
            style={[styles.drinkWise, {tintColor: brandChrome.onGround}]}
          />
          <View style={styles.rsaText}>
            {/* ID25 statement (taxonomy 346:32765). */}
            <Text style={styles.rsaSentence}>
              {brandChrome.name} supports the Responsible Service of Alcohol. Liquor Act 2007. It is
              an offence to sell or supply alcohol to, or to obtain alcohol on behalf of, a person
              aged under 18 years.
            </Text>
            <View style={styles.rsaLinks}>
              {rsaLinksShown.map((link, i) => (
                <React.Fragment key={link.label}>
                  {i > 0 && <Text style={styles.rsaSeparator}>|</Text>}
                  <Link link={link} style={styles.rsaLink} />
                </React.Fragment>
              ))}
            </View>
          </View>
        </View>
      </View>
      <View style={styles.legalBar}>
        <View
          style={[styles.inner, styles.legalInner, mobile && mobileStyles.legalInner]}
          testID="web-footer-legal-bar">
          <View
            style={[styles.legalLinks, mobile && mobileStyles.legalLinks]}
            testID="web-footer-legal-links">
            {!!licence && <Text style={styles.legalText}>{`Liquor Licence No. ${licence}`}</Text>}
            {legalLinks.map(link => (
              <Link key={link.label} link={link} style={[styles.legalText, styles.underline]} />
            ))}
          </View>
          <Text style={styles.utilityText} testID="web-footer-copyright">
            {`© Copyright ${brandChrome.name} - Australia ${new Date().getFullYear()}. All rights reserved.`}
          </Text>
        </View>
      </View>
      {/* The last line of the page, on every layout. */}
      <VersionTab mobile={mobile} />
    </View>
  );
}

export default observer(WebFooter);

const styles = StyleSheet.create({
  root: {backgroundColor: brandChrome.ground},
  band: {width: '100%'},
  divided: {borderBottomWidth: control.borderWidth, borderBottomColor: brandChrome.divider},
  inner: {
    width: '100%',
    maxWidth: PAGE_MAX_WIDTH,
    alignSelf: 'center',
    paddingHorizontal: space.s64,
  },
  linksBand: {flexDirection: 'row', gap: space.s24, paddingVertical: space.s24},
  column: {maxWidth: COLUMN_MAX_WIDTH, gap: space.s32},
  // Desktop only. react-native-web writes `flex: N` as CSS `flex: N`, whose flex-basis is 0%: in
  // the stacked mobile band inside a ScrollView that collapses a column to 0 height, and the
  // columns overprint. The mobile column takes no flex and is sized by its content.
  desktopColumn: {flex: 1},
  // Figma: heading → list is 32 in three columns and 16 in this one (recorded, kept).
  appColumn: {gap: space.s16},
  heading: {...chromeType.label16Medium, color: brandChrome.onGround},
  links: {gap: space.s16},
  link: {...type.button.category, ...lineHeight.button.category, color: brandChrome.onGround},
  // The 44 targets touch; the negative margin keeps the icons on the column's edges.
  socials: {flexDirection: 'row', alignItems: 'center', margin: -SOCIAL_HIT_INSET},
  socialTarget: {
    width: control.inputHeight,
    height: control.inputHeight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badges: {flexDirection: 'row', flexWrap: 'wrap', gap: space.s16, alignItems: 'flex-start'},
  googleBadgeSlot: {height: BADGE_HEIGHT, justifyContent: 'center'},
  googleBadgeBox: {
    width: APPLE_BADGE_WIDTH,
    height: (GOOGLE_PNG.height - 2 * GOOGLE_PNG.inset) * googleScale,
    overflow: 'hidden',
  },
  googleBadgeImage: {
    width: GOOGLE_PNG.width * googleScale,
    height: GOOGLE_PNG.height * googleScale,
    margin: -GOOGLE_PNG.inset * googleScale,
  },
  // footer/desk 476:7181 (161:7741): logo and text centred, gap 19.
  rsaBand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: RSA_GAP,
    paddingVertical: space.s24,
  },
  drinkWise: {width: DRINKWISE_WIDTH, height: DRINKWISE_HEIGHT},
  rsaText: {flex: 1, gap: space.s8},
  rsaSentence: {...chromeType.body12Light, color: brandChrome.onGround},
  rsaLinks: {flexDirection: 'row', gap: space.s12},
  rsaLink: {
    ...chromeType.label12Medium,
    color: brandChrome.onGround,
    textDecorationLine: 'underline',
  },
  rsaSeparator: {...chromeType.label12Medium, color: brandChrome.onGround},
  legalBar: {backgroundColor: universal.white},
  // Legal links left, copyright right; the copyright wraps below when the row is too narrow.
  legalInner: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    columnGap: space.s32,
    rowGap: space.s16,
    paddingVertical: space.s24,
  },
  legalLinks: {flexDirection: 'row', flexWrap: 'wrap', columnGap: space.s32, rowGap: space.s12},
  legalText: {
    ...type.paragraph.p2,
    ...lineHeight.paragraph.p2,
    color: brandChrome.legalText,
  },
  underline: {textDecorationLine: 'underline'},
  // Universal/Grey - Dark on white is 4.6:1: subdued, still AA for normal text.
  utilityText: {
    ...type.paragraph.p2,
    ...lineHeight.paragraph.p2,
    color: universal.greyDark,
  },
  // A full-width line, as the footer bands' dividers; the tab stands on it.
  versionRow: {
    alignItems: 'center',
    backgroundColor: universal.white,
    borderBottomWidth: control.borderWidth,
    borderBottomColor: universal.greyMedium,
  },
  versionInner: {alignItems: 'flex-end'},
  versionTab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.s6,
    paddingHorizontal: space.s12,
    paddingTop: space.s4,
    paddingBottom: space.s2,
    borderTopLeftRadius: VERSION_TAB_RADIUS,
    borderTopRightRadius: VERSION_TAB_RADIUS,
    backgroundColor: universal.white,
    borderWidth: control.borderWidth,
    borderBottomWidth: 0,
    borderColor: universal.greyMedium,
    // Overlaps the row's line by its width, so the line opens into the tab.
    marginBottom: -control.borderWidth,
  },
  // Universal/Grey - Dark on white is 4.6:1, as utilityText.
  versionText: {...type.paragraph.p3, color: universal.greyDark},
  envText: resolveWeight('bold'),
});

/** footer/mob 161:8697, applied over `styles`. */
const mobileStyles = StyleSheet.create({
  linksBand: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: space.s40,
    paddingHorizontal: space.s16,
  },
  stack: {flexDirection: 'column', alignItems: 'flex-start', paddingHorizontal: space.s16},
  legalInner: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: space.s24,
    paddingHorizontal: MOBILE_LEGAL_PADDING_X,
  },
  legalLinks: {flexDirection: 'column', gap: space.s16},
  versionInner: {paddingHorizontal: MOBILE_LEGAL_PADDING_X},
  column: {width: '100%', maxWidth: MOBILE_COLUMN_WIDTH, gap: space.s16},
});
